import './integration-action.scss';
import { observer } from 'mobx-react';
import { FormFieldLine } from '../../../../../components/form-field-line/form-field-line';
import { Input } from '../../../../../components/inputs/input/input';
import { SelectDropdown } from '../../../../../components/inputs/select-dropdown/select-dropdown';
import { Toggle } from '../../../../../components/inputs/toggle/toggle';
import { IValidationGroup } from '../../../../../hooks/validation-group/validation-group-hook';
import IntegrationSettingStore from '../../stores/integration-setting-store';
import { INTEGRATION_ACTION_INPUT_TYPE } from '../../../../../models/IntegrationsTypes';

interface Props {
    vGroup: IValidationGroup,
    actionStore: IntegrationSettingStore,
    isDisabled: boolean,
}

const getToggleInputComponent = (actionStore: IntegrationSettingStore, vGroup: IValidationGroup, isDisabled: boolean) => {
    if (typeof actionStore.currentValue === 'string') {
        return (
            <div className='toggle-input-container'>
                <Toggle 
                    checked={!!actionStore.currentValue}
                    label="" 
                    onChange={v => actionStore.setCurrentValue(v)}
                    disabled={isDisabled}
                />
                <Input
                    value={actionStore.currentValue as string}
                    placeholder=''
                    onChange={v => {
                        actionStore.setCurrentValue(v as string)
                    }}
                    validationGroup={vGroup}
                    required
                />
            </div>
        );
    }
    
    return (
        <Toggle 
            checked={actionStore.currentValue as boolean}
            label="" 
            onChange={v => {
                if (v) {
                    actionStore.setCurrentValue(actionStore.default);
                } else {
                    actionStore.setCurrentValue(v)
                }
            }}
            disabled={isDisabled}
        />
    );
}

const getInputComponent = (actionStore: IntegrationSettingStore, vGroup: IValidationGroup, isDisabled: boolean) => {
    return (
            <Input
                value={actionStore.currentValue as string}
                placeholder=''
                onChange={v => {
                    actionStore.setCurrentValue(v as string)
                }}
                validationGroup={vGroup}
                required={!actionStore.isOptional}
            />
    );
}

const getSpecificComponent = (actionStore: IntegrationSettingStore, vGroup: IValidationGroup, isDisabled: boolean) => {
    switch (actionStore.type) {
        case INTEGRATION_ACTION_INPUT_TYPE.TOGGLE_INPUT:
            return getToggleInputComponent(actionStore, vGroup, isDisabled);
        case INTEGRATION_ACTION_INPUT_TYPE.INPUT:
            return getInputComponent(actionStore, vGroup, isDisabled);
        case INTEGRATION_ACTION_INPUT_TYPE.DROPDOWN:
            return (
                <>
                    <SelectDropdown
                        validationGroup={vGroup}
                        isSearchable={false}
                        value={{
                            value: actionStore.currentValue as string,
                            label: actionStore.dropDownValueName,
                        }}
                        options={actionStore.dropDownValues ? actionStore.dropDownValues.map(value => ({
                            value: value.id,
                            label: value.label,
                        })) : []}
                        onChange={v => actionStore.setCurrentValue(v?.value as string)}
                        placeholder={`Select ${actionStore.label}`}
                        disabled={isDisabled}
                        note={actionStore.note}
                        required={!actionStore.isOptional}
                    />
                </>
            );
        default /* INTEGRATION_ACTION_INPUT_TYPE.TOGGLE */:
            return (
                <Toggle 
                    checked={actionStore.currentValue as boolean}
                    label="" 
                    onChange={v => actionStore.setCurrentValue(v)}
                    disabled={isDisabled}
                />
            );
    }
};

const IntegrationAction: React.FC<Props> = observer(({ vGroup, actionStore, isDisabled }) => (
    <FormFieldLine label={`${actionStore.label}`} note={actionStore.description} required={!actionStore.isOptional}>
        {getSpecificComponent(actionStore, vGroup, isDisabled)}
    </FormFieldLine>
));

export default IntegrationAction;
